/**
 * FormSection Component
 * Provides consistent section layout for configuration forms
 */

import * as React from 'react';
import { cn } from '../../../lib/utils';

export interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * FormSection displays a titled section with optional description
 * Used to group related form fields
 */
export function FormSection({
  title,
  description,
  children,
  className,
}: FormSectionProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export interface FormFieldProps {
  label: string;
  description?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  htmlFor?: string;
}

/**
 * FormField provides label, description, and error display for form inputs
 */
export function FormField({
  label,
  description,
  error,
  required,
  children,
  htmlFor,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-gray-900"
      >
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </label>
      {description && (
        <p className="text-sm text-gray-600">{description}</p>
      )}
      {children}
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
